import { useEffect, useId, useRef, useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { useSubmit } from '../../hooks/useSubmit';
import { refundOrder } from '../adminApi';
import { Field } from '../ui/Field';
import { AutoTextarea } from '../ui/AutoTextarea';
import { btnIcon, btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { Spinner } from '../../ui/Spinner';
import { useRequestId } from '../../hooks/useRequestId';
import { paymentMethodLabel, refundProblem, refundableCents } from '../../core/rules/revenue';
import { linesCents, refundedQty } from './orderLabels';
import { money } from '../../core/format/currency';
import type { AdminOrder, RefundLine } from '../../core/types/adminRequests';
type Props = {
  order: AdminOrder;
  onDone: (order: AdminOrder) => void;
  onFailed: () => void;
  onClose: () => void;
};

/**
 * Terugbetalen: alles wat nog openstaat, of per regel. Eerst invullen, dan één keer bevestigen.
 * Eén `requestId` per poging; zolang bedrag en regels gelijk blijven, gaat een herhaling (na een
 * verloren antwoord) met dezelfde id, zodat Mollie nooit twee keer uitbetaalt.
 */
export function RefundForm({
  order,
  onDone,
  onFailed,
  onClose
}: Props) {
  const reasonId = useId();
  const errorId = useId();
  const left = refundableCents(order);
  const already = refundedQty(order);
  const [mode, setMode] = useState<'all' | 'lines'>('all');
  const [qty, setQty] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(refundOrder);
  const lines: RefundLine[] = order.lines.filter(l => (qty[l.productId] ?? 0) > 0).map(l => ({
    productId: l.productId,
    qty: qty[l.productId]
  }));
  const amountCents = mode === 'all' ? left : Math.min(left, linesCents(order, lines));
  const problem = refundProblem(order, amountCents, reason);

  // Zelfde inhoud = zelfde poging. Verandert bedrag of regels, dan een nieuwe id.
  const attempt = `${mode}|${amountCents}|${JSON.stringify(lines)}|${restock}`;
  const requestId = useRequestId(attempt);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);
  const send = async () => {
    const result = await submit(order.id, {
      requestId,
      amountCents,
      reason: reason.trim(),
      ...(mode === 'lines' && lines.length ? {
        lines
      } : {}),
      ...(restock ? {
        restock: true
      } : {})
    });
    if (result) return onDone(result);
    setConfirming(false);
    onFailed();
    // Terug in het formulier: focus op de melding (of het redenveld), niet verloren op <body>.
    requestAnimationFrame(() => (document.getElementById(`${errorId}-form`) ?? document.getElementById(reasonId))?.focus());
  };
  const reasonError = fieldErrors.reason;
  const formError = error && !reasonError ? error : null;
  return <div className="rounded-lg border border-line bg-shell p-4 sm:p-5" data-id="element-3538" data-mp="1vjyod21wwj80">
      <h3 className="text-[15px] font-medium text-ink" data-id="element-3539" data-mp="25rjhdiau46em">Terugbetalen</h3>
      <p className="mt-0.5 text-[13px] text-muted" data-id="element-3540" data-mp="20q3v5mwx7tun">
        Nog terug te betalen: <span className="tabular-nums text-ink" data-id="element-3541" data-mp="1vrsp7n9qy3hp">{money(left)}</span>. Gaat via Mollie terug naar {paymentMethodLabel[order.paymentMethod]}.
      </p>

      {!confirming ? <div className="mt-4 space-y-4" data-id="element-3542" data-mp="1rstlu73nyesd">
          <fieldset data-id="element-3543" data-mp="2ogzk8c9zsay7">
            <legend className="text-[13px] font-medium text-ink" data-id="element-3544" data-mp="2og7yrua3ztln">Wat betaal je terug?</legend>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-[14px]" data-id="element-3545" data-mp="2jixytkp3fzjk">
              <label className="flex items-center gap-2" data-id="element-3546" data-mp="2eq4uuz1g8v8j">
                <input type="radio" name="refund-mode" checked={mode === 'all'} onChange={() => setMode('all')} className="h-4 w-4 accent-clay" data-id="element-3547" data-mp="2eok31h6irqed" />
                Alles ({money(left)})
              </label>
              <label className="flex items-center gap-2" data-id="element-3548" data-mp="32gir71r2hg8x">
                <input type="radio" name="refund-mode" checked={mode === 'lines'} onChange={() => setMode('lines')} className="h-4 w-4 accent-clay" data-id="element-3549" data-mp="32exw9b0ubvaj" />
                Per product
              </label>
            </div>
          </fieldset>

          {mode === 'lines' && <ul className="divide-y divide-line border-y border-line" data-id="element-3550" data-mp="2dyky5nt4bhqf">
              {order.lines.map(l => {
          const max = Math.max(0, l.qty - (already[l.productId] ?? 0));
          const n = qty[l.productId] ?? 0;
          const set = (v: number) => setQty(q => ({
            ...q,
            [l.productId]: Math.min(max, Math.max(0, v))
          }));
          return <li key={l.productId} className="flex items-center gap-3 px-3 py-2.5 text-[14px]" data-id="element-3551" data-mp="2wwx4l1snadib">
                    <span className="min-w-0 flex-1" data-id="element-3552" data-mp="30y0jnm2vc5ih">
                      <span className="block truncate text-ink" data-id="element-3553" data-mp="30wfte8nl78qf">{l.name}</span>
                      <span className="block text-[12px] text-muted" data-id="element-3554" data-mp="3on1okzchsyj0">
                        {money(l.unitPriceCents)} per stuk{max < l.qty && ` · ${l.qty - max} al terug`}
                      </span>
                    </span>
                    <span className="flex items-center gap-1" role="group" aria-label={`Aantal ${l.name}`} data-id="element-3555" data-mp="1hxhsdaqvtmu8">
                      <button type="button" onClick={() => set(n - 1)} disabled={n === 0} className={btnIcon} aria-label={`Eén minder ${l.name}`} data-id="element-3556" data-mp="1cynbwfge0ada">
                        <MinusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3557" data-mp="1mrccgfaeg3tu" />
                      </button>
                      <span className="w-10 text-center tabular-nums" aria-live="polite" data-id="element-3558" data-mp="z0yslc1yxbac">
                        {n} / {max}
                      </span>
                      <button type="button" onClick={() => set(n + 1)} disabled={n >= max} className={btnIcon} aria-label={`Eén meer ${l.name}`} data-id="element-3559" data-mp="18zfomnfrkg39">
                        <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3560" data-mp="13yi78d2kvq2t" />
                      </button>
                    </span>
                  </li>;
        })}
            </ul>}

          <label className="flex items-start gap-2.5 text-[14px] text-ink" data-id="element-3561" data-mp="2mi6m31xc48xt">
            <input type="checkbox" checked={restock} onChange={e => setRestock(e.target.checked)} className="mt-0.5 h-4 w-4 accent-clay" data-id="element-3562" data-mp="2mk98zd36xlyb" />
            <span data-id="element-3563" data-mp="2wft6u51beupp">
              Terug in de webshopvoorraad
              <span className="block text-[12px] text-muted" data-id="element-3564" data-mp="2wei0pykedavf">Alleen als het ongeopend terug is. Geopend of niet teruggestuurd: uit laten.</span>
            </span>
          </label>
          {order.voucherCents > 0 && <p className="text-[12px] text-muted" data-id="element-3565" data-mp="28qibx46nbujh">
              Deels betaald met een cadeaubon ({money(order.voucherCents)}): dat deel komt naar verhouding terug op de bon.
            </p>}

          <Field label="Reden" hint="Staat bij de terugbetaling en in de boekhouding. De klant ziet hem niet." data-id="element-3566" data-mp="2igbc791c8n8f">
            <AutoTextarea id={reasonId} value={reason} onChange={e => setReason(e.target.value)} minRows={2} maxLength={300} className={inputCls} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? errorId : undefined} data-id="element-3567" data-mp="1l1p8ynrlwmfd" />
          </Field>
          {reasonError && <p id={errorId} role="alert" className="-mt-2 text-[12px] text-clay" data-id="element-3568" data-mp="3u8kdf8qs0v42">
              {reasonError}
            </p>}
          {formError && <p id={`${errorId}-form`} tabIndex={-1} role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink outline-none" data-id="element-3569" data-mp="3kbfqv7t0qxmq">
              {formError}
            </p>}

          <div className="flex flex-wrap gap-2" data-id="element-3570" data-mp="3bj49ebfvukiw">
            <button type="button" onClick={() => setConfirming(true)} disabled={problem !== null} className={btnPrimary} data-id="element-3571" data-mp="3bjvtap04attc">
              {money(amountCents)} terugbetalen…
            </button>
            <button type="button" onClick={onClose} className={btnSecondary} data-id="element-3572" data-mp="31kojgbsldhaz">
              Niet terugbetalen
            </button>
          </div>
          {mode === 'lines' && amountCents === 0 ? <p className="text-[12px] text-muted" data-id="element-3573" data-mp="pben5qgrkmcy">Kies minstens één product.</p> : problem?.field === 'reason' && <p className="text-[12px] text-muted" data-id="element-3574" data-mp="ta5mm48pcg38">Vul eerst een reden in om verder te gaan.</p>}
        </div> : <div className="mt-4 rounded-md bg-sand px-3 py-3 text-[14px]" role="alertdialog" aria-label="Terugbetaling bevestigen" aria-describedby={`${errorId}-confirm`} data-id="element-3575" data-mp="kbafmpxu31yv">
          <p id={`${errorId}-confirm`} className="text-ink" data-id="element-3576" data-mp="5l2vm5s6haw2">
            <strong className="font-medium" data-id="element-3577" data-mp="aefiaadar7m6">{money(amountCents)}</strong> terugbetalen aan {order.customerName || order.email}? Dit kan niet ongedaan worden
            gemaakt. De klant krijgt een mail.
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-3578" data-mp="1lkbvdc3f5ut">
            <button ref={confirmRef} type="button" onClick={() => void send()} disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-3579" data-mp="3isxnm4bus923">
              {submitting && <Spinner data-id="element-3580" data-mp="3nud4dk6zzteo" />}
              {submitting ? 'Bezig met terugbetalen…' : 'Ja, terugbetalen'}
            </button>
            <button type="button" onClick={() => setConfirming(false)} disabled={submitting} className={btnSecondary} data-id="element-3581" data-mp="305ez15sfab18">
              Terug
            </button>
          </div>
        </div>}
    </div>;
}